import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const DRAWER_SELECTOR = '#cart-drawer';

function collectCartSectionIds() {
  const ids = [];
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.push(component.dataset.sectionId);
    }
  }
  return ids;
}

async function refreshCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (cartItemsComponent) {
    await customElements.whenDefined('cart-items-component');
    return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

export async function addLineFromDrawer({ source, variantId, quantity, properties }) {
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    }),
  );

  const line = { id: Number(variantId), quantity };
  if (properties) line.properties = properties;

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [line], sections: collectCartSectionIds().join(',') }),
    });

    const result = await response.json();

    if (result.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: result.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: result.description, errors: result.errors },
        }),
      );

      const erroredCart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(erroredCart),
        detail: {
          didError: true,
          items: erroredCart.items,
          source: 'cart-drawer-recommendations',
          itemCount: quantity,
        },
      });
      return false;
    }

    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'cart-drawer-recommendations',
        itemCount: quantity,
        sections: result.sections,
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      }),
    );
    return false;
  }
}

function setAddLabel(button, text) {
  if (!text) return;
  const label = button.querySelector('.bubble-button__label') ?? button;
  label.textContent = text;
}

function getCard(target) {
  return target instanceof Element ? target.closest('[data-reco-card]') : null;
}

function getVariantCarrier(card) {
  return card.querySelector('[data-reco-swatch]:checked') ?? card.querySelector('[data-reco-variant-id]');
}

function readMaxQuantity(card) {
  const parsed = Number.parseInt(getVariantCarrier(card)?.dataset.recoMax ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Infinity;
}

function readQuantity(card) {
  const input = card.querySelector('[data-reco-quantity]');
  const parsed = Number.parseInt(input?.value ?? '1', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function resolveVariantId(card) {
  const carrier = getVariantCarrier(card);
  return carrier?.value ?? null;
}

function applyQuantity(card, quantity) {
  const input = card.querySelector('[data-reco-quantity]');
  if (!input) return;

  const max = readMaxQuantity(card);
  const next = Math.min(Math.max(1, quantity), max);

  input.value = String(next);
  input.max = Number.isFinite(max) ? String(max) : '';

  const decrease = card.querySelector('[data-reco-quantity-decrease]');
  const increase = card.querySelector('[data-reco-quantity-increase]');
  if (decrease) decrease.disabled = next <= 1;
  if (increase) increase.disabled = next >= max;
}

function setQuantity(card, delta) {
  applyQuantity(card, readQuantity(card) + delta);
}

document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(DRAWER_SELECTOR)) return;

  const card = getCard(target);
  if (!card) return;

  if (target.closest('[data-reco-quantity-increase]')) {
    setQuantity(card, 1);
    return;
  }

  if (target.closest('[data-reco-quantity-decrease]')) {
    setQuantity(card, -1);
    return;
  }

  const addButton = target.closest('[data-reco-add]');
  if (!addButton || addButton.disabled) return;

  const variantId = resolveVariantId(card);
  if (!variantId) return;

  addButton.setAttribute('aria-busy', 'true');
  setAddLabel(addButton, addButton.dataset.recoAddingLabel);

  const added = await addLineFromDrawer({
    source: card,
    variantId,
    quantity: readQuantity(card),
    properties: card.hasAttribute('data-gift-card') ? { _promo_gift: 'cart-drawer' } : undefined,
  });

  addButton.removeAttribute('aria-busy');

  if (!added) {
    setAddLabel(addButton, addButton.dataset.recoAddLabel);
    return;
  }

  card.hidden = true;
});

const RAIL_SELECTOR = `${DRAWER_SELECTOR} .cart-reco__rail`;
const DRAG_THRESHOLD = 5;
const DRAG_FRICTION = 0.94;
const DRAG_VELOCITY_SMOOTHING = 0.7;
const DRAG_MIN_VELOCITY = 0.4;

let dragRail = null;
let dragPointerId = null;
let dragStartX = 0;
let dragStartScroll = 0;
let dragLastX = 0;
let dragVelocity = 0;
let dragMomentumFrame = null;
let dragged = false;

function stopMomentum() {
  if (dragMomentumFrame === null) return;
  cancelAnimationFrame(dragMomentumFrame);
  dragMomentumFrame = null;
}

function runMomentum(rail) {
  const maxScroll = rail.scrollWidth - rail.clientWidth;

  const step = () => {
    dragVelocity *= DRAG_FRICTION;

    const next = rail.scrollLeft - dragVelocity;
    const clamped = Math.min(Math.max(next, 0), maxScroll);
    rail.scrollLeft = clamped;

    if (Math.abs(dragVelocity) < DRAG_MIN_VELOCITY || clamped !== next) {
      dragMomentumFrame = null;
      rail.removeAttribute('data-dragging');
      return;
    }

    dragMomentumFrame = requestAnimationFrame(step);
  };

  dragMomentumFrame = requestAnimationFrame(step);
}

function endDrag(event) {
  if (!dragRail || event.pointerId !== dragPointerId) return;

  dragRail.removeEventListener('pointermove', onDragMove);
  dragRail.removeEventListener('pointerup', endDrag);
  dragRail.removeEventListener('pointercancel', endDrag);

  if (dragRail.hasPointerCapture(dragPointerId)) dragRail.releasePointerCapture(dragPointerId);

  const rail = dragRail;
  const glide = dragged && Math.abs(dragVelocity) >= DRAG_MIN_VELOCITY;
  dragRail = null;
  dragPointerId = null;

  requestAnimationFrame(() => {
    dragged = false;
  });

  if (glide) runMomentum(rail);
  else rail.removeAttribute('data-dragging');
}

function onDragMove(event) {
  if (!dragRail || event.pointerId !== dragPointerId) return;

  const delta = event.clientX - dragStartX;
  if (!dragged && Math.abs(delta) < DRAG_THRESHOLD) return;

  if (!dragged) {
    dragged = true;
    dragRail.setAttribute('data-dragging', '');
    try {
      dragRail.setPointerCapture(dragPointerId);
    } catch {}
  }

  dragVelocity = dragVelocity * DRAG_VELOCITY_SMOOTHING + (event.clientX - dragLastX) * (1 - DRAG_VELOCITY_SMOOTHING);
  dragLastX = event.clientX;
  dragRail.scrollLeft = dragStartScroll - delta;
}

document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;

  const target = event.target;
  if (!(target instanceof Element) || target.closest('[data-reco-quantity]')) return;

  const rail = target.closest(RAIL_SELECTOR);
  if (!rail || rail.scrollWidth <= rail.clientWidth) return;

  stopMomentum();
  rail.removeAttribute('data-dragging');

  dragRail = rail;
  dragPointerId = event.pointerId;
  dragStartX = event.clientX;
  dragLastX = event.clientX;
  dragStartScroll = rail.scrollLeft;
  dragVelocity = 0;
  dragged = false;

  rail.addEventListener('pointermove', onDragMove);
  rail.addEventListener('pointerup', endDrag);
  rail.addEventListener('pointercancel', endDrag);
});

document.addEventListener(
  'click',
  (event) => {
    if (!dragged) return;
    event.preventDefault();
    event.stopPropagation();
  },
  true,
);

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(DRAWER_SELECTOR)) return;

  const card = getCard(target);
  if (!card) return;

  if (target.matches('[data-reco-swatch]')) {
    const addButton = card.querySelector('[data-reco-add]');
    if (addButton) {
      addButton.disabled = false;
      document.dispatchEvent(new CustomEvent('bubble-button:init', { detail: { root: card } }));
    }
    applyQuantity(card, readQuantity(card));
    return;
  }

  if (target.matches('[data-reco-quantity]')) {
    applyQuantity(card, readQuantity(card));
  }
});
